import React, { useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Spell type display info
const SPELL_TYPE_INFO = {
  damage: { label: 'Damage', color: '#e74c3c', icon: '🔥', friendly: false },
  heal: { label: 'Heal', color: '#27ae60', icon: '💚', friendly: true },
  buff_att: { label: 'Buff ATT', color: '#f39c12', icon: '⬆️', friendly: true },
  buff_def: { label: 'Buff DEF', color: '#3498db', icon: '🛡️', friendly: true },
  buff_stat: { label: 'Buff Stat', color: '#9b59b6', icon: '📈', friendly: true },
  debuff_def: { label: 'Debuff DEF', color: '#8e44ad', icon: '📉', friendly: false },
  debuff_att: { label: 'Debuff ATT', color: '#c0392b', icon: '📉', friendly: false },
  debuff_miss: { label: 'Miss Chance', color: '#7f8c8d', icon: '🌀', friendly: false },
  hp_divide: { label: 'HP Divide', color: '#2c3e50', icon: '💀', friendly: false },
  mirror_image: { label: 'Mirror', color: '#1abc9c', icon: '👥', friendly: true },
  divination: { label: 'Divination', color: '#f1c40f', icon: '👁️', friendly: true },
  travel: { label: 'Travel', color: '#16a085', icon: '🌀', friendly: true },
  combo_damage: { label: 'Combo', color: '#e67e22', icon: '💥', friendly: false },
  drain: { label: 'Drain', color: '#8e44ad', icon: '🩸', friendly: false },
  combo_buff: { label: 'Combo Buff', color: '#2ecc71', icon: '✨', friendly: true },
  combo_debuff: { label: 'Combo Debuff', color: '#e74c3c', icon: '💢', friendly: false },
  stat_up: { label: 'Stat Up', color: '#3498db', icon: '⬆️', friendly: true },
  stat_down: { label: 'Stat Down', color: '#e74c3c', icon: '⬇️', friendly: false },
};

const ActionsPanel = ({
  character,
  nearbyPlayers = [],
  spottedMonster,
  attackPending,
  onAttackMonster,
  onUseItem,
  onUseItemOnPlayer,
  onCastSpell,
  onCastSpellOnPlayer,
  onCastSpellAtMonster,
  onLogMessage,
  onDismissPet
}) => {
  // View state: main | spells | items
  const [mode, setMode] = useState('main');
  const [spells, setSpells] = useState([]);
  const [spellsLoading, setSpellsLoading] = useState(false);
  const [spellFilter, setSpellFilter] = useState('all');
  const [spellSearch, setSpellSearch] = useState('');

  // Targeting state
  const [pendingAction, setPendingAction] = useState(null); // { type: 'spell'|'item', data: spell|item, isFriendly: bool }

  // Fetch spells
  const fetchSpells = useCallback(async () => {
    setSpellsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/available-spells`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const equipped = data.equippedSpells || [];
        const available = (data.spells || []).filter(s =>
          equipped.length === 0 || s.equipped
        );
        setSpells(available);
      }
    } catch (err) {
      console.error('Error fetching spells:', err);
    }
    setSpellsLoading(false);
  }, []);

  if (!character) return null;

  // Parse inventory
  let inventory = [];
  try {
    inventory = typeof character.inventory === 'string'
      ? JSON.parse(character.inventory)
      : (character.inventory || []);
  } catch (e) { inventory = []; }

  // Usable items: restore (potions), buff (alchemy), scroll (warp)
  const usableItems = inventory.filter(item =>
    item.type === 'restore' || item.type === 'scroll' || item.type === 'buff'
  );

  const currentHp = (character.max_hp || 0) - (character.damage || 0);
  const currentMp = (character.max_mp || 0) - (character.used_mp || 0);
  const isHurt = currentHp < (character.max_hp || 1);

  const handleOpenSpells = () => {
    setMode('spells');
    setPendingAction(null);
    fetchSpells();
  };

  // Filter spells
  const filteredSpells = spells.filter(spell => {
    const info = SPELL_TYPE_INFO[spell.type] || {};
    if (spellFilter === 'friendly' && !info.friendly) return false;
    if (spellFilter === 'hostile' && info.friendly) return false;
    if (spellSearch && !spell.name.toLowerCase().includes(spellSearch.toLowerCase())) return false;
    return true;
  });

  const canAfford = (mpCost) => currentMp >= mpCost;

  // ── TARGETING LOGIC ──

  // When a spell is clicked, determine if we need target selection
  const handleSpellClick = (spell) => {
    const info = SPELL_TYPE_INFO[spell.type] || { friendly: false };

    if (info.friendly) {
      // Friendly spell: show target picker (Self + nearby players)
      if (nearbyPlayers.length > 0) {
        setPendingAction({ type: 'spell', data: spell, isFriendly: true });
      } else {
        // No nearby players — auto-target self
        onCastSpell && onCastSpell(spell.name);
        setPendingAction(null);
        setMode('main');
      }
    } else {
      // Hostile spell: target monster
      if (spottedMonster) {
        onCastSpellAtMonster && onCastSpellAtMonster(spell.name, spottedMonster.monster.name);
        setPendingAction(null);
        setMode('main');
      } else {
        onLogMessage && onLogMessage(`${spell.name} is an offensive spell — you need a monster target!`);
      }
    }
  };

  // When an item is clicked, determine if we need target selection
  const handleItemClick = (item) => {
    const isRestorative = item.type === 'restore' && ((item.hp && item.hp > 0) || (item.mp && item.mp > 0));

    if (isRestorative && nearbyPlayers.length > 0) {
      // Restorative item with nearby players: offer target choice
      setPendingAction({ type: 'item', data: item, isFriendly: true });
    } else {
      // Scrolls, buffs, or no nearby players: use on self
      onUseItem && onUseItem(item.name);
      setPendingAction(null);
      setMode('main');
    }
  };

  // Target selection handlers
  const handleTargetSelf = () => {
    if (!pendingAction) return;
    if (pendingAction.type === 'spell') {
      onCastSpell && onCastSpell(pendingAction.data.name);
    } else if (pendingAction.type === 'item') {
      onUseItem && onUseItem(pendingAction.data.name);
    }
    setPendingAction(null);
    setMode('main');
  };

  const handleTargetPlayer = (player) => {
    if (!pendingAction) return;
    if (pendingAction.type === 'spell') {
      onCastSpellOnPlayer && onCastSpellOnPlayer(pendingAction.data.name, player.id, player.name);
    } else if (pendingAction.type === 'item') {
      onUseItemOnPlayer && onUseItemOnPlayer(pendingAction.data.name, player.id, player.name);
    }
    setPendingAction(null);
    setMode('main');
  };

  const handleCancelTarget = () => {
    setPendingAction(null);
  };

  // ── Styles ──
  const btnStyle = {
    padding: '0.35rem 0.5rem',
    fontSize: '0.8rem',
    background: 'var(--bg-panel-alt, #444)',
    border: '1px solid var(--border-dark, #555)',
    borderRadius: '4px',
    color: 'var(--text-primary, #ddd)',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'var(--font-ui, inherit)',
    transition: 'background 0.15s',
  };

  const targetBtnStyle = {
    padding: '0.4rem 0.6rem',
    fontSize: '0.78rem',
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid',
    fontFamily: 'var(--font-ui, inherit)',
    transition: 'all 0.15s',
    textAlign: 'center',
    flex: 1,
    minWidth: 0,
  };

  // ── TARGET PICKER OVERLAY ──
  const renderTargetPicker = () => {
    if (!pendingAction) return null;

    const actionLabel = pendingAction.type === 'spell'
      ? `🔮 ${pendingAction.data.name}`
      : `💊 ${pendingAction.data.name}`;

    return (
      <div style={{
        background: 'linear-gradient(135deg, #1a2a1a 0%, #1a1a2a 100%)',
        border: '1px solid #4a6a4a',
        borderRadius: '5px',
        padding: '0.5rem',
        marginBottom: '0.5rem',
        animation: 'fadeIn 0.2s ease',
      }}>
        <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem' }}>
          Use on whom?
        </div>
        <div style={{ fontSize: '0.82rem', color: '#ddd', fontWeight: 'bold', marginBottom: '0.4rem' }}>
          {actionLabel}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {/* Self button */}
          <button
            style={{
              ...targetBtnStyle,
              background: '#1a3a1a',
              borderColor: '#2a6a2a',
              color: '#8f8',
            }}
            onClick={handleTargetSelf}
          >
            🧑 Self ({character.name})
          </button>

          {/* Nearby player buttons */}
          {nearbyPlayers.map((player, idx) => (
            <button
              key={`target-${player.id || idx}`}
              style={{
                ...targetBtnStyle,
                background: '#1a1a3a',
                borderColor: '#2a2a6a',
                color: '#88f',
              }}
              onClick={() => handleTargetPlayer(player)}
            >
              👤 {player.name} <span style={{ fontSize: '0.65rem', color: '#666' }}>
                Lv.{player.level} {player.advanced_class || player.class}
              </span>
            </button>
          ))}

          {/* Cancel */}
          <button
            style={{
              ...targetBtnStyle,
              background: '#2a1a1a',
              borderColor: '#4a2a2a',
              color: '#a88',
              fontSize: '0.7rem',
            }}
            onClick={handleCancelTarget}
          >
            ✕ Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="actions-panel">
      <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Actions
        {mode !== 'main' && !pendingAction && (
          <button
            style={{ ...btnStyle, width: 'auto', fontSize: '0.7rem', padding: '2px 8px' }}
            onClick={() => { setMode('main'); setPendingAction(null); }}
          >
            ⬅️ Back
          </button>
        )}
      </h3>

      {/* ══════ TARGET PICKER (overlays current view) ══════ */}
      {pendingAction && renderTargetPicker()}

      {/* ══════ MAIN VIEW ══════ */}
      {mode === 'main' && !pendingAction && (
        <>
          {/* Monster encounter card */}
          {spottedMonster && (
            <div style={{
              marginBottom: '0.5rem',
              background: 'linear-gradient(135deg, #2a1515 0%, #1a1a2a 100%)',
              border: '1px solid #8b3a3a',
              borderRadius: '5px',
              padding: '0.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <img
                  src={`/images/monsters/${spottedMonster.monster.pic}`}
                  alt={spottedMonster.monster.name}
                  onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
                  style={{
                    width: '48px', height: '48px', borderRadius: '4px',
                    border: '1px solid #666', objectFit: 'cover', flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#e74c3c' }}>
                    ⚔️ {spottedMonster.monster.name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#aaa' }}>
                    Level {spottedMonster.monster.level}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: '0.4rem', fontStyle: 'italic' }}>
                {spottedMonster.message}
              </div>
              <button
                style={{
                  ...btnStyle, background: attackPending ? '#555' : '#c0392b', color: 'white', fontWeight: 'bold',
                  border: `1px solid ${attackPending ? '#777' : '#e74c3c'}`, textAlign: 'center', fontSize: '0.82rem',
                  opacity: attackPending ? 0.7 : 1, cursor: attackPending ? 'not-allowed' : 'pointer',
                }}
                disabled={attackPending}
                onClick={() => onAttackMonster && onAttackMonster(spottedMonster.monster.name)}
              >
                {attackPending ? '⏳ Starting Battle...' : '⚔️ Engage in Battle'}
              </button>
              <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '0.3rem', textAlign: 'center' }}>
                Move away to ignore
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.4rem' }}>
            {/* Cast Spell button */}
            <button
              style={{ ...btnStyle, background: '#1a2a5a', borderColor: '#2a4a8a' }}
              onClick={handleOpenSpells}
            >
              <span style={{ marginRight: '0.3rem' }}>🔮</span>
              Cast Spell
              {spells.length > 0 && <span style={{ float: 'right', color: '#666', fontSize: '0.7rem' }}>
                {spells.length}
              </span>}
            </button>

            {/* Use Item button */}
            <button
              style={{ ...btnStyle, background: '#1a3a1a', borderColor: '#2a5a2a' }}
              onClick={() => { setMode('items'); setPendingAction(null); }}
            >
              <span style={{ marginRight: '0.3rem' }}>💊</span>
              Use Item
              {usableItems.length > 0 && <span style={{ float: 'right', color: '#666', fontSize: '0.7rem' }}>
                {usableItems.length}
              </span>}
            </button>
          </div>

          {/* Dismiss Pet button (Druid with pet only) */}
          {character.class === 'Druid' && character.pet_data && (() => {
            let pet = null;
            try { pet = typeof character.pet_data === 'string' ? JSON.parse(character.pet_data) : character.pet_data; } catch (e) { /* */ }
            return pet ? (
              <div style={{ marginTop: '0.2rem' }}>
                <button
                  style={{ ...btnStyle, background: '#3a2a1a', borderColor: '#6b4a2a', fontSize: '0.78rem' }}
                  onClick={() => {
                    if (window.confirm(`Dismiss ${pet.name}? It will leave permanently.`)) {
                      onDismissPet && onDismissPet();
                    }
                  }}
                >
                  <span style={{ marginRight: '0.3rem' }}>🐾</span>
                  Dismiss {pet.name}
                </button>
              </div>
            ) : null;
          })()}

          {/* Quick Items — potions at a glance */}
          {usableItems.length > 0 && (
            <div style={{ marginTop: '0.1rem' }}>
              <div style={{ color: '#839dbf', fontSize: '0.68rem', marginBottom: '0.2rem' }}>
                Quick Use:
              </div>
              <div style={{
                display: 'flex', gap: '3px', flexWrap: 'wrap',
                padding: '0.25rem', background: '#111', borderRadius: '3px', border: '1px solid #222',
              }}>
                {usableItems.slice(0, 8).map((item, idx) => {
                  const isHP = item.name?.toLowerCase().includes('health') || (item.hp && item.hp > 0 && !item.mp);
                  const isMP = item.name?.toLowerCase().includes('mana') || (item.mp && item.mp > 0 && !item.hp);
                  const isBuff = item.type === 'buff';
                  const isScroll = item.type === 'scroll';
                  const bgColor = isHP ? '#6b1a1a' : isMP ? '#1a1a6b' : isBuff ? '#4a3a1a' : isScroll ? '#1a4a2a' : '#333';
                  const icon = isHP ? '❤️' : isMP ? '💧' : isBuff ? '⚗️' : isScroll ? '📜' : '📦';
                  return (
                    <div
                      key={`qi-${idx}-${item.name}`}
                      style={{
                        minWidth: '36px', height: '30px', background: bgColor,
                        borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '0.6rem', cursor: 'pointer', border: '1px solid #444',
                        padding: '0 3px', flexDirection: 'column', lineHeight: 1,
                      }}
                      title={`${item.name} (${item.quantity || 1})`}
                      onClick={() => handleItemClick(item)}
                    >
                      <span style={{ fontSize: '0.7rem' }}>{icon}</span>
                      {(item.quantity || 1) > 1 && (
                        <span style={{ fontSize: '0.45rem', opacity: 0.7 }}>x{item.quantity}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Nearby Players indicator */}
          {nearbyPlayers.length > 0 && (
            <div style={{
              marginTop: '0.4rem', padding: '0.3rem', background: '#1a1a2a',
              border: '1px solid #2a2a4a', borderRadius: '3px', fontSize: '0.7rem',
            }}>
              <span style={{ color: '#839dbf' }}>👥 Nearby: </span>
              {nearbyPlayers.map((p, i) => (
                <span key={i} style={{ color: '#aac' }}>
                  {p.name}{i < nearbyPlayers.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}

          {/* Quick hints */}
          {isHurt && !spottedMonster && (
            <div style={{
              marginTop: '0.3rem', padding: '0.25rem 0.3rem', background: '#1a2a1a',
              border: '1px solid #2a4a2a', borderRadius: '3px', fontSize: '0.68rem', color: '#6a6',
            }}>
              ❤️ You're hurt ({currentHp}/{character.max_hp} HP)
            </div>
          )}
        </>
      )}

      {/* ══════ SPELL VIEW ══════ */}
      {mode === 'spells' && !pendingAction && (
        <div>
          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '3px', marginBottom: '0.3rem' }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'friendly', label: '💚 Helpful' },
              { key: 'hostile', label: '🔥 Hostile' },
            ].map(f => (
              <button
                key={f.key}
                style={{
                  ...btnStyle, width: 'auto', textAlign: 'center', fontSize: '0.7rem', padding: '2px 6px',
                  background: spellFilter === f.key ? '#3a3a5a' : '#1a1a2a',
                  borderColor: spellFilter === f.key ? '#6a6a8a' : '#2a2a3a',
                }}
                onClick={() => setSpellFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search spells..."
            value={spellSearch}
            onChange={e => setSpellSearch(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '0.25rem',
              marginBottom: '0.3rem', background: '#111', border: '1px solid #2a2a3a',
              borderRadius: '3px', color: '#ccc', fontSize: '0.72rem',
            }}
          />

          {spellsLoading ? (
            <div style={{ color: '#888', fontSize: '0.8rem', textAlign: 'center', padding: '1rem' }}>
              Loading spells...
            </div>
          ) : filteredSpells.length === 0 ? (
            <div style={{ color: '#666', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem' }}>
              {spells.length === 0 ? 'No spells available' : 'No spells match filter'}
            </div>
          ) : (
            <div style={{
              maxHeight: '280px', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: '2px',
            }}>
              {filteredSpells.map((spell, idx) => {
                const info = SPELL_TYPE_INFO[spell.type] || { label: spell.type, color: '#666', icon: '✨', friendly: false };
                const affordable = canAfford(spell.mp);
                const needsTarget = !info.friendly;
                const hasTarget = !!spottedMonster;
                const canUse = affordable && (info.friendly || hasTarget);

                return (
                  <div
                    key={`spell-${idx}`}
                    onClick={() => canUse && handleSpellClick(spell)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.3rem 0.4rem',
                      background: !affordable ? '#111' : (info.friendly ? '#0d1a0d' : '#1a0d0d'),
                      border: `1px solid ${!canUse ? '#1a1a1a' : info.color + '44'}`,
                      borderRadius: '4px',
                      cursor: canUse ? 'pointer' : 'not-allowed',
                      opacity: canUse ? 1 : 0.45,
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ fontSize: '0.85rem', flexShrink: 0 }}>{info.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.76rem', fontWeight: 'bold', color: canUse ? '#ddd' : '#666' }}>
                        {spell.name}
                      </div>
                      <div style={{ fontSize: '0.6rem', color: '#777', display: 'flex', gap: '0.3rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          background: info.color, color: '#fff', padding: '0 4px',
                          borderRadius: '2px', fontSize: '0.52rem', lineHeight: '1.4',
                        }}>
                          {info.label}
                        </span>
                        {info.friendly && (
                          <span style={{ color: '#4a8', fontSize: '0.52rem' }}>
                            {nearbyPlayers.length > 0 ? '🎯 targetable' : '→ self'}
                          </span>
                        )}
                        {needsTarget && !hasTarget && (
                          <span style={{ color: '#a55', fontStyle: 'italic' }}>needs monster</span>
                        )}
                        {spell.element && (
                          <span style={{ color: '#666' }}>{spell.element}</span>
                        )}
                      </div>
                    </div>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 'bold', flexShrink: 0,
                      color: affordable ? '#4080d0' : '#c0392b',
                    }}>
                      {spell.mp} MP
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: '0.58rem', color: '#555', marginTop: '0.3rem', textAlign: 'center' }}>
            💚 Helpful → self or ally | 🔥 Hostile → monster
          </div>
        </div>
      )}

      {/* ══════ ITEMS VIEW ══════ */}
      {mode === 'items' && !pendingAction && (
        <div>
          {usableItems.length === 0 ? (
            <div style={{ color: '#666', fontSize: '0.8rem', textAlign: 'center', padding: '1rem' }}>
              No usable items in inventory
            </div>
          ) : (
            <div style={{
              maxHeight: '300px', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: '2px',
            }}>
              {usableItems.map((item, idx) => {
                const isHP = item.name?.toLowerCase().includes('health') || (item.hp && item.hp > 0);
                const isMP = item.name?.toLowerCase().includes('mana') || (item.mp && item.mp > 0 && !item.hp);
                const isBuff = item.type === 'buff';
                const isScroll = item.type === 'scroll';
                const isRestorative = item.type === 'restore' && ((item.hp > 0) || (item.mp > 0));
                const bgColor = isHP ? '#1a0d0d' : isMP ? '#0d0d1a' : isBuff ? '#1a1a0d' : isScroll ? '#0d1a0d' : '#111';
                const icon = isHP ? '❤️' : isMP ? '💧' : isBuff ? '⚗️' : isScroll ? '📜' : '📦';

                return (
                  <div
                    key={`item-${idx}-${item.name}`}
                    onClick={() => handleItemClick(item)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.35rem 0.4rem', background: bgColor,
                      border: '1px solid #222', borderRadius: '4px',
                      cursor: 'pointer', transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.76rem', fontWeight: 'bold', color: '#ddd' }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize: '0.6rem', color: '#777', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        {item.hp ? <span>+{item.hp} HP</span> : null}
                        {item.mp ? <span>+{item.mp} MP</span> : null}
                        {isBuff && <span style={{ color: '#f39c12' }}>buff</span>}
                        {isScroll && <span>{item.description || 'Scroll'}</span>}
                        {isRestorative && nearbyPlayers.length > 0 && (
                          <span style={{ color: '#4a8', fontSize: '0.52rem' }}>🎯 targetable</span>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.7rem', color: '#888', flexShrink: 0 }}>
                      x{item.quantity || 1}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {nearbyPlayers.length > 0 && (
            <div style={{ fontSize: '0.58rem', color: '#555', marginTop: '0.3rem', textAlign: 'center' }}>
              💊 Potions can target yourself or a nearby ally
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionsPanel;
